import Cuboid from '../rendering/Cuboid.js';
import Debug from '../utils/Debug.js';

/**
 * Handles face picking (click/hover) on cuboids using DOM events
 */
export default class FacePicker {
  /**
   * Create a new face picker
   * @param {World} world - The world instance
   * @param {MaterialEditor} materialEditor - The material editor instance
   */
  constructor(world, materialEditor) {
    this.world = world;
    this.materialEditor = materialEditor;
    this.enabled = true;
    this.hoveredElement = null;
    this.mouseDownTarget = null;
    this.mouseDownPos = null;

    // Mobile long press state for multi-selection
    this.longPressTimer = null;
    this.longPressDelay = 400; // ms to wait before triggering multi-select
    this.longPressTouchStart = null; // { x, y, faceEl, cuboid, face }
    this.touchWasCameraDrag = false; // Track if touch was used for camera movement
    this.longPressTriggered = false; // Track if long press completed
    this.isLongPressActive = false; // Shared flag: InputManager checks this to avoid stealing touch events

    this.setupEventListeners();
  }

  /**
   * Set up event listeners using event delegation on the container
   */
  setupEventListeners() {
    const containerEl = document.getElementById('container');
    if (!containerEl) {
      Debug.error('FacePicker', '#container element not found');
      return;
    }

    // Track mousedown position and target
    containerEl.addEventListener('mousedown', this.handleMouseDown.bind(this), true);

    // Handle selection on mouseup (only if no drag occurred)
    containerEl.addEventListener('mouseup', this.handleMouseUp.bind(this), true);

    // Mouse move handler for hover feedback
    containerEl.addEventListener('mousemove', this.handleMouseMove.bind(this));

    // Mouse leave handler to clear hover state
    containerEl.addEventListener('mouseleave', this.handleMouseLeave.bind(this));

    // Touch event handlers for mobile long press multi-selection
    containerEl.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
    containerEl.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    containerEl.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: false });
    containerEl.addEventListener('touchcancel', this.handleTouchEnd.bind(this), { passive: false });
  }

  /**
   * Handle mousedown events - track starting position
   * @param {MouseEvent} event - The mousedown event
   */
  handleMouseDown(event) {
    if (!this.enabled) return;
    if (event.button !== 0) return; // Only left click

    // Store the target and position
    this.mouseDownTarget = event.target;
    this.mouseDownPos = { x: event.clientX, y: event.clientY };
  }

  /**
   * Handle mouseup events - select face if no drag occurred
   * @param {MouseEvent} event - The mouseup event
   */
  handleMouseUp(event) {
    if (!this.enabled) return;
    if (event.button !== 0) return; // Only left click

    const CLICK_THRESHOLD = 5; // Max pixels of movement to count as a click

    // Check if this is a click (minimal movement)
    if (!this.mouseDownPos) return;

    const dx = event.clientX - this.mouseDownPos.x;
    const dy = event.clientY - this.mouseDownPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Clear stored state
    this.mouseDownTarget = null;
    this.mouseDownPos = null;

    // If mouse moved too much, it's a drag not a click
    if (distance > CLICK_THRESHOLD) return;

    // Find the clicked face element
    const faceEl = event.target.closest('.face');
    if (!faceEl) return;

    // Find the parent cube element
    const cubeEl = faceEl.closest('.cube');
    if (!cubeEl) return;

    // Determine which face was clicked
    const face = this.getFaceType(faceEl);
    if (!face) return;

    // Find the cuboid instance
    const cuboid = this.world.findCuboidByElement(cubeEl);
    if (!cuboid) {
      Debug.warn('FacePicker', 'Could not find cuboid for element', cubeEl);
      Debug.warn('FacePicker', 'cubeEl classList:', cubeEl.className);
      Debug.warn('FacePicker', 'cubeEl in DOM?', document.body.contains(cubeEl));
      Debug.warn('FacePicker', 'world element children count:', this.world.element.childElementCount);
      return;
    }

    // Select the face (with Shift support for multi-selection)
    this.selectFace(cuboid, face, event.shiftKey);
  }

  /**
   * Handle mouse move events for hover feedback
   * @param {MouseEvent} event - The mouse move event
   */
  handleMouseMove(event) {
    if (!this.enabled) return;

    // Find the hovered face element
    const faceEl = event.target.closest('.face');

    // If we're hovering over the same element, do nothing
    if (faceEl === this.hoveredElement) return;

    // Clear previous hover state
    if (this.hoveredElement) {
      this.hoveredElement.classList.remove('face-hovered');
    }

    // Set new hover state
    if (faceEl) {
      faceEl.classList.add('face-hovered');
      this.hoveredElement = faceEl;
    } else {
      this.hoveredElement = null;
    }
  }

  /**
   * Handle mouse leave events to clear hover state
   */
  handleMouseLeave() {
    if (this.hoveredElement) {
      this.hoveredElement.classList.remove('face-hovered');
      this.hoveredElement = null;
    }
  }

  /**
   * Handle touch start for mobile long press multi-selection
   * @param {TouchEvent} event - The touch start event
   */
  handleTouchStart(event) {
    if (!this.enabled) return;

    // Multi-touch (pinch zoom) - cancel any pending long press
    if (event.touches.length > 1) {
      this._cancelLongPress();
      this.touchWasCameraDrag = true;
      return;
    }

    // Only handle single-finger touch
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    const target = document.elementFromPoint(touch.clientX, touch.clientY);

    // Reset state for new touch
    this.touchWasCameraDrag = false;
    this.longPressTriggered = false;

    // Don't intercept if touching UI
    if (target?.closest('.sidebar-panel, .top-toolbar, .top-sheet, .settings-panel') || target?.closest('.info-panel')) return;

    // Check if touching a face
    const faceEl = target?.closest('.face');
    if (!faceEl) return;

    // Don't handle ghost preview faces
    if (faceEl.closest('.ghost-preview')) return;

    // Find the parent cube element
    const cubeEl = faceEl.closest('.cube');
    if (!cubeEl) return;

    // Determine which face was touched
    const face = this.getFaceType(faceEl);
    if (!face) return;

    // Find the cuboid instance
    const cuboid = this.world.findCuboidByElement(cubeEl);
    if (!cuboid) return;

    // Cancel any existing long press
    this._cancelLongPress();

    // Store touch start info for long press detection
    this.longPressTouchStart = {
      x: touch.clientX,
      y: touch.clientY,
      faceEl: faceEl,
      cuboid: cuboid,
      face: face
    };

    // Signal to InputManager that a face long-press is in progress
    // so it won't steal touch events by entering camera drag mode
    this.isLongPressActive = true;

    // Add visual feedback for long press
    faceEl.classList.add('face-long-press');

    // Start long press timer - multi-selection triggers after delay
    this.longPressTimer = setTimeout(() => {
      if (!this.longPressTouchStart) return;

      const { faceEl, cuboid, face } = this.longPressTouchStart;

      // Remove visual feedback
      faceEl.classList.remove('face-long-press');
      this.longPressTimer = null;
      this.longPressTriggered = true;
      this.isLongPressActive = false;

      // Trigger multi-selection (like shift+click)
      this.selectFace(cuboid, face, true);
    }, this.longPressDelay);
  }

  /**
   * Handle touch move - cancel long press if user drags (camera movement)
   * @param {TouchEvent} event - The touch move event
   */
  handleTouchMove(event) {
    if (!this.enabled) return;

    // Multi-touch - cancel long press
    if (event.touches.length > 1) {
      this._cancelLongPress();
      this.touchWasCameraDrag = true;
      return;
    }

    // Check for movement beyond threshold
    if (this.longPressTouchStart && event.touches.length === 1) {
      const touch = event.touches[0];
      const dx = touch.clientX - this.longPressTouchStart.x;
      const dy = touch.clientY - this.longPressTouchStart.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // If moved more than 10px, it's a camera drag - cancel long press
      if (distance > 10) {
        this._cancelLongPress();
        this.touchWasCameraDrag = true;
      }
    }
  }

  /**
   * Handle touch end - single tap selection if long press didn't trigger
   * @param {TouchEvent} event - The touch end event
   */
  handleTouchEnd(event) {
    if (!this.enabled) return;

    // If it was a camera drag or long press already triggered, just clean up
    if (this.touchWasCameraDrag || this.longPressTriggered) {
      this._cancelLongPress();
      return;
    }

    // If long press timer is still active, it's a short tap - do single selection
    if (this.longPressTouchStart && this.longPressTimer) {
      const { cuboid, face } = this.longPressTouchStart;

      // Cancel the timer
      this._cancelLongPress();

      // Single selection (not shift-held)
      this.selectFace(cuboid, face, false);
    } else {
      this._cancelLongPress();
    }
  }

  /**
   * Cancel any pending long press
   * @private
   */
  _cancelLongPress() {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
    // Remove visual feedback
    if (this.longPressTouchStart?.faceEl) {
      this.longPressTouchStart.faceEl.classList.remove('face-long-press');
    }
    this.longPressTouchStart = null;
    this.isLongPressActive = false;
  }

  /**
   * Get the face type from a face element's class list
   * @param {HTMLElement} faceEl - The face element
   * @return {string|null} The face type or null
   */
  getFaceType(faceEl) {
    const faceTypes = ['top', 'bottom', 'front', 'back', 'left', 'right'];
    for (const type of faceTypes) {
      if (faceEl.classList.contains(type)) {
        return type;
      }
    }
    return null;
  }

  /**
   * Select a face on a cuboid
   * @param {Cuboid} cuboid - The cuboid instance
   * @param {string} face - The face name
   * @param {boolean} isShiftHeld - Whether Shift key is held for multi-selection
   */
  selectFace(cuboid, face, isShiftHeld = false) {
    // For fragments, use parent key so selection and groups work consistently
    const cuboidKey = cuboid.isFragment ? cuboid.parentKey : cuboid.getKey();
    const serializedFace = `${cuboidKey}|${face}`;

    // Initialize selectedFaces Set if it doesn't exist
    if (!Cuboid.selectedFaces) {
      Cuboid.selectedFaces = new Set();
    }

    if (isShiftHeld) {
      // Multi-selection mode: toggle face in selection
      if (Cuboid.selectedFaces.has(serializedFace)) {
        // Remove from selection
        Cuboid.selectedFaces.delete(serializedFace);

        // Update primary selection
        if (Cuboid.primarySelectedFace === serializedFace) {
          // Pick another as primary, or null
          Cuboid.primarySelectedFace = Cuboid.selectedFaces.size > 0
            ? Array.from(Cuboid.selectedFaces)[Cuboid.selectedFaces.size - 1]
            : null;
        }
      } else {
        // Add to selection
        Cuboid.selectedFaces.add(serializedFace);
        Cuboid.primarySelectedFace = serializedFace;
      }
    } else {
      // Single selection mode: clear and select only this face
      Cuboid.selectedFaces.clear();
      Cuboid.selectedFaces.add(serializedFace);
      Cuboid.primarySelectedFace = serializedFace;
    }

    // Increment selection version to trigger highlight updates on all cuboids
    Cuboid.selectionVersion++;

    // Update legacy selectedFace for backward compatibility
    if (Cuboid.primarySelectedFace) {
      const separatorIndex = Cuboid.primarySelectedFace.lastIndexOf('|');
      Cuboid.selectedFace = {
        cuboidKey: Cuboid.primarySelectedFace.substring(0, separatorIndex),
        face: Cuboid.primarySelectedFace.substring(separatorIndex + 1)
      };
    } else {
      Cuboid.selectedFace = null;
    }

    // If material editor is already open, update it to show this face
    if (this.materialEditor && this.materialEditor.isOpen()) {
      this.materialEditor.onFaceSelected(cuboidKey, face);
    }

    // Force world update to apply visual highlighting
    // forceTransformUpdate ensures cuboid.update() runs even in a static scene
    this.world.forceTransformUpdate();
    this.world.update();
  }

  /**
   * Check if a face is currently selected
   * @param {string} cuboidKey - The cuboid key
   * @param {string} face - The face name
   * @return {boolean} True if face is selected
   */
  isSelected(cuboidKey, face) {
    if (!Cuboid.selectedFaces) return false;
    return Cuboid.selectedFaces.has(`${cuboidKey}|${face}`);
  }

  /**
   * Get all selected faces
   * @return {Array} Array of { cuboidKey, face } objects
   */
  getSelectedFaces() {
    if (!Cuboid.selectedFaces) return [];
    return Array.from(Cuboid.selectedFaces).map(s => {
      const separatorIndex = s.lastIndexOf('|');
      return {
        cuboidKey: s.substring(0, separatorIndex),
        face: s.substring(separatorIndex + 1)
      };
    });
  }

  /**
   * Clear all selections
   */
  clearSelection() {
    if (Cuboid.selectedFaces) {
      Cuboid.selectedFaces.clear();
    }
    Cuboid.primarySelectedFace = null;
    Cuboid.selectedFace = null;
    Cuboid.selectionVersion++;
  }

  /**
   * Enable or disable the face picker
   * @param {boolean} enabled - Whether to enable the picker
   */
  setEnabled(enabled) {
    this.enabled = enabled;

    // Clear hover state when disabling
    if (!enabled && this.hoveredElement) {
      this.hoveredElement.classList.remove('face-hovered');
      this.hoveredElement = null;
    }

    // Clear touch state when disabling
    if (!enabled) {
      this._cancelLongPress();
    }
  }
}
